import React, {Fragment, Component} from 'react';
import { Box, Container, Typography } from '@mui/material';
import Button from '../components/Button/Button';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import {AppLogo, AppLogoImg, DescriptionBox, GlobalPrintStyles} from '../styles/jss/components/thankyou';
import ReviewData from "./ReviewData";
import { ButtonGroup, CenteredBox, MainTitle, ReviewBox, ReviewList, ReviewListItem, ReviewListLabel, ReviewListText, ReviewTitle, ReviewWrapper, SecondaryText } from '../styles/jss/global';
import {Component as AppComponent} from "../enum/Component";
import {ValidationUtil} from "../util/ValidationUtil";

class ThankYou extends Component {

    classes: any;
    componentProps: any;

    constructor(props: any) {
        super(props);
        this.classes = props.classes;
        this.componentProps = {
            showHeader: false,
            app: {
                logo: props.state.app.logo,
                paymentSuccessMessage: props.state.app?.payment?.config?.messages?.paymentSuccessMessage,
                hasSuccessPurchase: props.state.hasSuccessPurchase,
                config: props.state.app.config,
                template: props.state.app.template
            },
            appData: props.state.appData,
            rootSchema: props.state.app.schemas,
            uniqueIds: props.state.uniqueIds,
            paymentDetails: props.state.paymentDetails
        }
    }

    componentDidMount() {
        const thankYouPageReloadTimer = this.componentProps.app.config?.thankYouPageReloadTimer;
        if (thankYouPageReloadTimer !== undefined && thankYouPageReloadTimer !== 0) {
            setTimeout(() => {
                window.location.reload();
            }, thankYouPageReloadTimer * 1000);
        }
    }

    /**
     * Check if we should show Google Review prompt
     * Returns { show: boolean, message: string, url: string } or null
     */
    getGoogleReviewInfo = () => {
        const dataSchema = this.componentProps.rootSchema.dataSchema;
        const appData = this.componentProps.appData;

        // Search through all pages and components for a rating with Google review enabled
        for (const page of Object.keys(dataSchema.properties)) {
            const pageProperties = dataSchema.properties[page].properties;

            for (const component of Object.keys(pageProperties)) {
                const componentSchema = pageProperties[component];

                // Check if this component has a 'rating' property
                if (componentSchema.properties?.rating) {
                    const ratingSchema = componentSchema.properties.rating;

                    // Check if Google review is enabled
                    if (ratingSchema.googleReviewEnabled === true) {
                        // Get the actual rating value from appData
                        const ratingValue = appData?.[page]?.[component]?.rating;

                        if (!ValidationUtil.isEmptyValue(ratingValue)) {
                            const rating = parseInt(ratingValue) || 0;
                            const threshold = ratingSchema.googleReviewThreshold || 4;

                            // Only show if rating meets threshold
                            if (rating >= threshold) {
                                const placeId = ratingSchema.googlePlaceId;
                                if (ValidationUtil.isEmptyValue(placeId)) {
                                    return null;
                                }
                                const message = ratingSchema.googleReviewMessage ||
                                    "Thank you for your positive feedback! Would you mind sharing your experience on Google to help others?";

                                return {
                                    show: true,
                                    message: message,
                                    url: `https://search.google.com/local/writereview?placeid=${placeId}`
                                };
                            }
                        }
                    }
                }
            }
        }

        console.log('No Google review prompt to show');
        return null;
    }


    render() {
        if (!this.componentProps.app || !this.componentProps.appData) {
            return <div>Loading...</div>;
        }

        const print = () => {
            window.print();
        }

        const googleReviewInfo = this.getGoogleReviewInfo();

        return (
            <Fragment>
                <GlobalPrintStyles />

                <Header {...this.componentProps} />
                {this.componentProps.app.logo &&
                    <CenteredBox component="main" maxWidth="sm">
                        <AppLogo>
                            <AppLogoImg src={this.componentProps.app.logo} alt="App logo" />
                        </AppLogo>
                    </CenteredBox>
                }
                <Container component="main" className="main" maxWidth="sm">
                    <Box>
                        <DescriptionBox>
                            <MainTitle variant={'h3'} component={'h1'}>Submission Complete</MainTitle>
                            <MainTitle variant={'h3'} component={'h1'}>Thank You!</MainTitle>
                            {this.componentProps.app.paymentSuccessMessage && this.componentProps.app.hasSuccessPurchase &&
                                <SecondaryText>{this.componentProps.app.paymentSuccessMessage}.</SecondaryText>
                            }
                        </DescriptionBox>

                        {/* Google Review Prompt */}
                        {googleReviewInfo && (
                            <Box
                                sx={{
                                    mt: 3,
                                    mb: 3,
                                    p: 3,
                                    textAlign: 'center',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: `2px solid ${this.componentProps.app.template?.colors?.primary || '#607d8b'}`
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 2,
                                        fontSize: '16px',
                                        color: '#333'
                                    }}
                                >
                                    {googleReviewInfo.message}
                                </Typography>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    size="large"
                                    disableElevation
                                    onClick={() => window.open(googleReviewInfo.url, '_blank', 'noopener,noreferrer')}
                                    sx={{ mt: 1 }}
                                >
                                    Leave a Google Review ⭐
                                </Button>
                            </Box>
                        )}

                        <ReviewWrapper>
                            {
                                <ReviewData
                                    appData={this.componentProps.appData}
                                    dataSchema={this.componentProps.rootSchema.dataSchema}
                                    uiSchemas={this.componentProps.rootSchema.uiSchema}
                                    showPages={[]}
                                    postSubmission={true}
                                    uniqueIds={this.componentProps.uniqueIds}
                                    appTemplate={this.componentProps.app.template}/>
                            }
                            {this.completedPaymentDetails()}
                        </ReviewWrapper>
                        <ButtonGroup>
                            <Button
                                color="secondary"
                                variant="outlined"
                                size="large"
                                disableElevation
                                classes={{root: 'button-group-item'}}
                                onClick={print}>
                                Print
                            </Button>
                            <Button
                                color="secondary"
                                variant="contained"
                                size="large"
                                disableElevation
                                classes={{root: 'button-group-item'}}
                                onClick={ () => { window.location.reload(); }}
                            >Start Again</Button>
                        </ButtonGroup>
                    </Box>
                </Container>

                <Footer />
            </Fragment>
        );
    }

    /**
     *
     * @private
     */
    private completedPaymentDetails() {
        let paymentDetails = this.componentProps.paymentDetails;

        if(Object.keys(paymentDetails).length === 0){
            return null
        }

        return (
            <ReviewBox>
                <ReviewTitle component={'h3'}>Payment Details</ReviewTitle>

                <ReviewList>
                    {Object.keys(paymentDetails).map((key: string, itemIndex: number) => {
                        let obj = paymentDetails[key];
                        return (
                            <ReviewListItem key={`thank-you-review-list-item-${itemIndex}`}>
                                <ReviewListText>
                                    <ReviewListLabel component="span">
                                        {obj.key} : </ReviewListLabel>

                                    {obj.value}
                                </ReviewListText>
                            </ReviewListItem>
                        );
                    })}
                </ReviewList>
            </ReviewBox>
        );
    }
}

export default ThankYou;
